import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FORM_ACTION = "https://readdy.ai/api/form/dbb6afk8sc1eisn2tnvg";

const channels = [
  { icon: "ri-mail-line", label: "资料纠错与合作", value: "archive@krfilm-demo.com", note: "工作日 24 小时内回复" },
  { icon: "ri-weibo-line", label: "新浪微博", value: "@韩国AV影视资料站", note: "更新速报与片单发布" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "krfilm-archive", note: "每周专题推送" },
  { icon: "ri-time-line", label: "内容更新时间", value: "每周一、周四", note: "节假日顺延" },
];

const faqs = [
  {
    q: "站点提供影片播放或下载吗？",
    a: "不提供。本站只整理作品资料与剧情介绍，页面中的资源索引按钮为界面演示，不会跳转到真实影片地址。",
  },
  {
    q: "可以帮忙补充某部作品的资料吗？",
    a: "可以。请通过本页表单说明作品名称与需要补充的字段，我们会核对后加入待整理清单。",
  },
  {
    q: "发现条目信息有误怎么办？",
    a: "欢迎直接在反馈表单中选择「资料纠错」，写明条目名称与错误位置即可，我们会在下一次更新时修正。",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  useDocumentMeta({
    title: "联系我们 - 韩国AV影视资料站",
    description:
      "通过反馈表单联系韩国AV影视资料站，提交资料纠错、作品补充建议或合作咨询。也提供邮箱与社交账号等联系方式。",
    keywords: "联系韩国AV,影视资料反馈,资料纠错,合作咨询,韩国影视网站",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessageLength(0);
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请完整填写姓名、邮箱与反馈内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("邮箱格式看起来不太对，请检查后再提交。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("反馈内容不能超过 500 个字符，请精简后再提交。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
        setMessageLength(0);
        return;
      }

      setStatus("error");
      setFormError(
        isSpam
          ? "提交未通过校验，请检查内容后重试。"
          : serverMessage || "提交失败，请稍后重试或直接发送邮件给我们。",
      );
    } catch {
      setStatus("error");
      setFormError("网络请求失败，请检查网络连接后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full bg-primary-950">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-14 lg:px-8">
            <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-accent-400">Contact</span>
            <h1 className="mt-3 font-heading text-[30px] font-semibold leading-tight text-background-50 md:text-[36px]">
              联系我们
            </h1>
            <p className="mt-3 max-w-2xl text-[13px] leading-8 text-primary-200">
              资料纠错、作品补充或合作咨询，都可以通过下面的渠道或表单告诉我们。我们更愿意把每一条反馈都看完。
            </p>
          </div>
        </section>

        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-6 px-4 py-10 md:px-6 lg:grid-cols-[1fr_1.15fr] lg:px-8 lg:py-14">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-[20px] font-semibold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              联系方式
            </h2>
            <p className="mt-2 text-[12px] leading-6 text-foreground-500">
              以下邮箱与账号为站点演示信息，用于展示联系模块的形态。
            </p>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {channels.map((channel) => (
                <div key={channel.label} className="rounded-lg border border-background-200/90 bg-background-50 p-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded bg-secondary-100 text-secondary-700">
                    <i className={`${channel.icon} text-sm`} />
                  </span>
                  <p className="mt-2.5 text-[11px] text-foreground-500">{channel.label}</p>
                  <p className="mt-0.5 break-all text-[12px] font-medium text-foreground-900">{channel.value}</p>
                  <p className="mt-1 text-[10px] text-foreground-400">{channel.note}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200/90 bg-background-100 p-5">
              <h3 className="text-[13px] font-semibold text-foreground-900">提交前请留意</h3>
              <ul className="mt-2.5 space-y-2 text-[12px] leading-6 text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-700" />
                  请写明作品名称，便于我们定位条目。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-700" />
                  反馈内容请控制在 500 字以内。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-700" />
                  本站不提供影片文件，请勿索取播放或下载地址。
                </li>
              </ul>
            </div>

            <div className="mt-6">
              <h2 className="font-heading text-[18px] font-semibold text-foreground-950">常见问题</h2>
              <div className="mt-3 space-y-2.5">
                {faqs.map((faq) => (
                  <details key={faq.q} className="group rounded-lg border border-background-200/90 bg-background-50 p-4">
                    <summary className="flex cursor-pointer items-center justify-between gap-3 text-[13px] font-medium text-foreground-900">
                      {faq.q}
                      <i className="ri-add-line shrink-0 text-foreground-400 transition-transform group-open:rotate-45" />
                    </summary>
                    <p className="mt-2.5 text-[12px] leading-7 text-foreground-600">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200/90 bg-background-50 p-5 md:p-7">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-[20px] font-semibold text-foreground-950">反馈表单</h2>
                <p className="mt-1 text-[12px] text-foreground-500">带 * 的为必填项，提交后会在本页显示结果。</p>
              </div>
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className="ri-chat-3-line text-base" />
              </span>
            </div>

            {status === "success" ? (
              <div className="mt-6 flex flex-col items-center rounded-lg border border-accent-200 bg-accent-50 px-5 py-12 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-primary-950">
                  <i className="ri-check-line text-xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-accent-900">感谢反馈</h3>
                <p className="mt-1.5 max-w-xs text-[12px] leading-6 text-accent-900/80">
                  我们已经收到你的内容，会在下一次资料更新时一并核对处理。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-edit-line text-sm" />
                  再提交一条
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="dbb6afk8sc1eisn2tnvg" className="mt-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label htmlFor="contact-name" className="mb-1.5 block text-[12px] font-medium text-foreground-700">
                      姓名 <span className="text-accent-700">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      autoComplete="name"
                      placeholder="怎么称呼你"
                      className="h-10 w-full box-border rounded-md border border-background-300 bg-background-50 px-3 text-[13px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="contact-email" className="mb-1.5 block text-[12px] font-medium text-foreground-700">
                      邮箱 <span className="text-accent-700">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="name@example.com"
                      className="h-10 w-full box-border rounded-md border border-background-300 bg-background-50 px-3 text-[13px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="mt-4 min-w-0">
                  <label htmlFor="contact-topic" className="mb-1.5 block text-[12px] font-medium text-foreground-700">
                    反馈类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="资料纠错"
                    className="h-10 w-full box-border rounded-md border border-background-300 bg-background-50 px-3 text-[13px] text-foreground-800 outline-none focus:border-primary-400"
                  >
                    <option value="资料纠错">资料纠错</option>
                    <option value="作品补充">作品补充</option>
                    <option value="专题建议">专题建议</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="mt-4 min-w-0">
                  <div className="mb-1.5 flex items-center justify-between">
                    <label htmlFor="contact-message" className="block text-[12px] font-medium text-foreground-700">
                      反馈内容 <span className="text-accent-700">*</span>
                    </label>
                    <span className={`text-[10px] ${messageLength > 500 ? "text-accent-700" : "text-foreground-400"}`}>
                      {messageLength} / 500
                    </span>
                  </div>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="请描述需要补充或修改的内容，例如作品名称、错误字段与正确信息。"
                    className="w-full box-border resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-[13px] leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="feedback-extra-field"
                />

                {status === "error" && formError && (
                  <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3 py-2.5 text-[12px] leading-6 text-accent-900">
                    <i className="ri-error-warning-line mt-0.5 text-sm" />
                    <span>{formError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-5 flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-sm" />
                      正在提交
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-sm" />
                      提交反馈
                    </>
                  )}
                </button>

                <p className="mt-3 text-center text-[10px] leading-5 text-foreground-400">
                  提交即表示同意我们使用该内容核对与改进站点资料。
                  <Link to="/about" className="ml-1 text-primary-700 hover:underline">
                    查看版权声明
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}